import React, { useState, useMemo } from "react";
import {
  Plus,
  Search,
  ChevronDown,
  Download,
  Filter,
  MoreVertical,
  Calendar,
  DollarSign,
  User,
  Hash,
  ArrowLeft,
  ArrowRight,
  Eye,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText
} from "lucide-react";
import { useInvoices } from "../../Hooks/useInvoices";
import InvoiceDetailsModal from "../profile/InvoiceDetailsModal";
import { canAccess } from "../../utils/permissions";

export default function Invoices() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { invoices, pagination, isLoading, error } = useInvoices(currentPage, pageSize);

  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      const search = searchTerm.toLowerCase();
      const clientName = `${inv.client?.first_name || ""} ${inv.client?.last_name || ""}`.toLowerCase();
      const invoiceId = `inv-#${inv.id}`.toLowerCase();
      const servicesArray = Array.isArray(inv.services) ? inv.services : [inv.services];
      const services = servicesArray.map(s => typeof s === 'object' ? s.name : s).join(", ").toLowerCase();

      const matchesSearch =
        inv.id.toString().includes(search) ||
        invoiceId.includes(search) ||
        clientName.includes(search) ||
        (inv.client?.phone || "").toLowerCase().includes(search) ||
        (inv.client?.email || "").toLowerCase().includes(search) ||
        services.includes(search);

      const matchesStatus = selectedStatus === "All" || inv.status === selectedStatus.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [invoices, searchTerm, selectedStatus]);

  const stats = [
    {
      label: "Total Invoices",
      value: pagination.total,
      icon: FileText,
      color: "bg-blue-50 text-blue-600",
      trend: "+12% this month"
    },
    {
      label: "Total Amount",
      value: `$${invoices.reduce((acc, inv) => acc + parseFloat(inv.total || 0), 0).toFixed(2)}`,
      icon: DollarSign,
      color: "bg-emerald-50 text-emerald-600",
      trend: "Recent batches"
    },
    {
      label: "Active Filters",
      value: selectedStatus === "All" ? "No Filter" : selectedStatus,
      icon: Filter,
      color: "bg-purple-50 text-purple-600",
      trend: "Based on results"
    }
  ];

  const getStatusBadge = (status) => {
    const styles = {
      paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
      pending: "bg-amber-100 text-amber-700 border-amber-200",
      overdue: "bg-red-100 text-red-700 border-red-200",
      default: "bg-gray-100 text-gray-700 border-gray-200"
    };

    const style = styles[status?.toLowerCase()] || styles.default;
    const Icon = status?.toLowerCase() === 'paid' ? CheckCircle2 : (status?.toLowerCase() === 'overdue' ? AlertCircle : Clock);

    return (
      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border flex items-center gap-1.5 w-fit ${style}`}>
        <Icon className="w-3 h-3" />
        {status || 'Unknown'}
      </span>
    );
  };

  const openInvoiceDetails = (id) => {
    setSelectedInvoiceId(id);
    setIsModalOpen(true);
  };

  if (!canAccess("view_invoices")) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <FileText className="w-10 h-10 text-red-600" />
        </div>
        <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
        <p className="text-gray-500 max-w-md font-medium">
          You don't have the required permissions to view invoices.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-10 text-center">
        <div className="bg-red-50 text-red-600 p-4 rounded-xl inline-block max-w-md border border-red-100">
          <AlertCircle className="w-8 h-8 mx-auto mb-2" />
          <p className="font-bold">Failed to load invoices</p>
          <p className="text-sm opacity-80">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <section className="min-h-screen p-4 sm:p-6 lg:p-8 space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Invoices</h1>
          <p className="text-gray-500 mt-1 font-medium italic">Monitor and manage all client billings and payments</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
            <div className={`absolute top-0 right-0 w-24 h-24 ${stat.color} opacity-10 rounded-bl-full transform translate-x-8 -translate-y-8 transition-transform group-hover:scale-110`} />
            <div className="flex items-center gap-4 relative">
              <div className={`p-4 rounded-2xl ${stat.color} shadow-sm group-hover:scale-105 transition-transform`}>
                <stat.icon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">{stat.label}</p>
                <p className="text-3xl font-black text-gray-900 mt-0.5">{stat.value}</p>
                <p className="text-[11px] font-medium text-gray-500 mt-1 underline decoration-gray-200 underline-offset-4">{stat.trend}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by ID, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-transparent focus:border-blue-500 focus:bg-white rounded-xl outline-none transition-all text-sm font-medium"
          />
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="flex bg-gray-50 p-1 rounded-xl w-full md:w-auto overflow-x-auto no-scrollbar">
            {["All", "Paid", "Pending", "Overdue"].map((status) => (
              <button
                key={status}
                onClick={() => setSelectedStatus(status)}
                className={`px-4 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${selectedStatus === status
                  ? "bg-white text-blue-600 shadow-sm ring-1 ring-gray-100"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="p-20 text-center">
            <div className="animate-spin w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full mx-auto" />
            <p className="mt-4 text-gray-500 font-bold animate-pulse">Loading invoices...</p>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="p-20 text-center">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-dashed border-gray-200">
              <Hash className="w-10 h-10 text-gray-300" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">No invoices found</h3>
            <p className="text-gray-500 max-w-xs mx-auto mt-2">Try adjusting your filters or search terms to find what you're looking for.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50/50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest">Invoice Details</th>
                  <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest">Client Informaton</th>
                  <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest">Amount</th>
                  <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest">Status</th>
                  <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest">Created At</th>
                  <th className="px-6 py-4 text-center text-[11px] font-bold text-gray-400 uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredInvoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-gray-50/50 transition-colors group cursor-pointer" onClick={() => openInvoiceDetails(inv.id)}>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-lg text-blue-600 group-hover:scale-110 transition-transform">
                          <FileText className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-sm font-black text-gray-900">INV-#{inv.id}</p>
                          <p className="text-xs text-gray-500 line-clamp-1 max-w-[200px]">
                            {(Array.isArray(inv.services) ? inv.services : (inv.services ? [inv.services] : []))
                              .map(s => typeof s === 'object' ? s.name : s)
                              .join(", ") || "No services listed"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-800">
                            {inv.client?.email || inv.client?.phone || `Client #${inv.client?.id}`}
                          </p>
                          <p className="text-[10px] text-gray-400 uppercase font-black tracking-tight">{inv.client?.phone || 'No phone'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div>
                        <p className="text-base font-black text-emerald-600">${parseFloat(inv.total).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                        <p className="text-[10px] text-gray-400 font-bold italic">USD Currency</p>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      {getStatusBadge(inv.status)}
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4 opacity-40" />
                        <span className="text-xs font-bold">
                          {inv.createdAt ? (() => {
                            const date = new Date(inv.createdAt);
                            return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                          })() : 'No date'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); openInvoiceDetails(inv.id); }}
                          className="p-2 hover:bg-white rounded-xl text-blue-600 transition shadow-sm border border-transparent hover:border-blue-100"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button className="p-2 hover:bg-white rounded-xl text-gray-400 hover:text-gray-600 transition shadow-sm border border-transparent hover:border-gray-200">
                          <Download className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="p-6 bg-gray-50/50 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              className="bg-white border border-gray-200 rounded-lg text-xs font-bold px-3 py-1.5 outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || isLoading}
              className="p-2 rounded-xl bg-white border border-gray-200 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition shadow-sm"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-1.5 px-4 font-black text-sm">
              <span className="text-blue-600 bg-blue-50 px-3 py-1 rounded-lg ring-1 ring-blue-100">{currentPage}</span>
              <span className="text-gray-400">/</span>
              <span className="text-gray-600">{pagination.lastPage}</span>
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(pagination.lastPage, prev + 1))}
              disabled={currentPage === pagination.lastPage || isLoading}
              className="p-2 rounded-xl bg-white border border-gray-200 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition shadow-sm"
            >
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <InvoiceDetailsModal
          invoiceId={selectedInvoiceId}
          onClose={() => { setIsModalOpen(false); setSelectedInvoiceId(null); }}
        />
      )}
    </section>
  );
}
